import { FormControl, Select, MenuItem, styled } from "@mui/material";
import { useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { IStoreState } from "Interfaces";
import { saveAuthToken } from "Helpers/saveAuthToken";
import { loginSuccess, setUser } from "Actions";
import { reinitializeApiServices, userService } from "Apis/api";
import { useSelectedProject } from "Hooks";
import { RouteNames } from "Hooks/routeNames";
import useProtectedRoutes from "Hooks/useProtectedRoutes";

const StyledFormControl = styled(FormControl)({
  minWidth: 120,
});

const StyledSelect = styled(Select)({
  color: "white",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#37345d",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(255, 255, 255, 1)",
  },
  "& .MuiSvgIcon-root": {
    color: "white",
  },
  "& .MuiSelect-select": {
    paddingTop: 8,
    paddingBottom: 8,
  },
});

function EnvironmentSelector() {
  const dispatch = useDispatch();
  const history = useHistory();
  const { pathByName } = useProtectedRoutes();
  const { wipeLastSelected } = useSelectedProject();
  const tokens = useSelector((state: IStoreState) => state.auth.tokens);

  const handleEnvironmentChange = useCallback(
    async (newEnv: string) => {
      if (!tokens || tokens.selectedEnv === newEnv) {
        return;
      }

      // Clear selected project/client as they may not exist in the new environment
      wipeLastSelected();

      // Update tokens with new environment
      const updatedTokens = { ...tokens, selectedEnv: newEnv };
      saveAuthToken(updatedTokens);
      dispatch(loginSuccess(updatedTokens));

      // Reinitialize API services with new environment
      reinitializeApiServices();

      // Refetch user data for the new environment
      try {
        const u = await userService.userServiceGetAuthUser();
        dispatch(setUser(u));
      } catch (userError) {
        console.warn(
          "Could not fetch user data after environment switch:",
          userError,
        );
      }

      history.push(pathByName(RouteNames.HOME));
      // Refresh the page to apply the new environment
      window.location.reload();
    },
    [tokens, dispatch, wipeLastSelected, history, pathByName],
  );

  // Get available environments and check if user has multiple
  const availableEnvs = tokens?.envs || [];
  const hasMultipleEnvs = availableEnvs.length > 1;
  const currentEnv = tokens?.selectedEnv;

  // Don't render if user doesn't have multiple environments
  if (!hasMultipleEnvs) {
    return null;
  }

  return (
    <StyledFormControl size="small">
      <StyledSelect
        value={currentEnv || ""}
        onChange={(e) => handleEnvironmentChange(e.target.value as string)}
        displayEmpty
        MenuProps={{
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "right",
          },
          transformOrigin: {
            vertical: "top",
            horizontal: "right",
          },
        }}
      >
        {availableEnvs.map((env) => (
          <MenuItem key={env} value={env}>
            {env}
          </MenuItem>
        ))}
      </StyledSelect>
    </StyledFormControl>
  );
}

export default EnvironmentSelector;
